<template>
    <div class="timeline">
        <div class="timeline-title">
            <h2 :title="timelineTooltip">Timeline</h2>
        </div>
        <div class="timeline-content">
            <comment-input
                @input="postComment"
                :is-processing="isPostProcessing || isProcessing"
                :suggestion-providers="suggestionProviders"/>
            <template v-if="!hasNoMessages">
                <message-list
                    :type="type"
                    :expanded="expanded"
                    :entity-id="entityId"
                    :groups="sortedGroups"
                    @delete-message="showDeleteDialog"
                    @download-attachment="downloadAttachment"/>
                <div class="text-center" v-if="hasMoreMessages">
                    <div class="divider"></div>
                    <r-button :disabled="isProcessing" @click="loadMore">Load more messages</r-button>
                </div>
                <div class="divider divider-hidden"></div>
            </template>
        </div>
        <div v-if="hasNoMessages" class="text-muted timeline-no-messages">There are no timeline messages for this
            {{typeLabel}}.
        </div>
    </div>
</template>

<script>
    import LoadStateMixin from '@/mixins/load-state-mixin';
    import TimelineMessageModel from '@/common/models/timeline-message-model';
    import AttachmentModel from '@/common/models/attachment-model';
    import {createDialog, vConfirm} from '@/components/modal/dialogs';
    import Media from '@/common/files/media';
    import MessageList from './components/message-list';
    import CommentInput from './components/comments/comment-input';
    import SuggestionProviders from './components/comments/suggestion-providers';
    import {TimelineTypeLabels, TimelineTypes, TimelineResourceMap} from '@/components/timeline/timeline-types';

    export default {
        name: 'timeline',
        mixins: [LoadStateMixin],
        components: {
            CommentInput,
            MessageList,
        },
        props: {
            type: {
                type: String,
                required: true,
                validator: type => Object.values(TimelineTypes).includes(type),
            },
            entity: {
                type: Object,
                required: true,
            },
            limit: {
                type: Number,
                default: 20,
            },
            expanded: {
                type: Boolean,
            },
        },
        computed: {
            typeLabel() {
                return TimelineTypeLabels[this.type] || this.type;
            },
            hasNoMessages() {
                return !this.messages.length;
            },
            sortedGroups() {
                const messages = [...this.messages];
                // sort messages newest first
                messages.sort(
                    (messageA, messageB) => (messageA.occurredTimeMoment < messageB.occurredTimeMoment ? 1 : -1),
                );

                // set message year markers
                let tmpYearMarker = null;

                // group messages per date
                return Object.values(messages.reduce((memo, message) => {
                    const dayValueOf = message.occurredTimeMoment
                        .clone()
                        .startOf('day')
                        .valueOf();

                    const messageYear = message.occurredTimeMoment.year();
                    const hasYearMarker = tmpYearMarker !== messageYear;

                    if (hasYearMarker) {
                        tmpYearMarker = messageYear;
                    }

                    memo[dayValueOf] = memo[dayValueOf] || {
                        date: dayValueOf,
                        hasYearMarker,
                        messages: [],
                    };
                    memo[dayValueOf].messages.push(message);
                    return memo;
                }, {}));
            },
            hasMoreMessages() {
                return this.displayedCount < this.totalCount;
            },
            timelineTooltip() {
                return `${this.totalCount} messages total`;
            },
            entityId() {
                return this.entity.id;
            },
        },
        data() {
            return {
                messages: [],
                totalCount: 0,
                offset: 0,
                displayedCount: this.limit,
                isProcessing: false,
                isPostProcessing: false,
                suggestionProviders: SuggestionProviders,
            };
        },
        methods: {
            update() {
                this.fetchData();
            },
            async fetchData() {
                this.messages = [];
                this.totalCount = 0;
                this.isProcessing = true;
                try {
                    const {messages, total} = await this.generateCall();
                    this.totalCount = total;
                    this.messages = messages;
                } catch (error) {
                    this.messages = [];
                    this.totalCount = 0;
                    // Todo: handling error, + undefined timeline type error
                } finally {
                    this.isProcessing = false;
                    this.stopLoadingPage();
                }
            },
            async generateCall() {
                const params = {
                    id: this.entity.id,
                    limit: this.limit,
                    sort: '-occurredTime',
                    offset: this.offset,
                };
                const {items: messageItems, total} = await this.getDelegate(api => api.getAllTimelineMessages(params));
                const attachments = await this.getMessageAttachments(messageItems);
                const messages = messageItems.map(
                    ({fields}) => new TimelineMessageModel(
                        {
                            ...fields,
                            attachments: attachments
                                .filter(({relatedId}) => relatedId === fields.id),
                        },
                    ),
                );
                return {messages, total};
            },
            async getDelegate(callback) {
                if (!TimelineResourceMap[this.type]) {
                    throw new Error(`Undefined timeline type ${this.type}`);
                }

                return this.$api(({[TimelineResourceMap[this.type]]: handler}) => callback(handler));
            },
            async getMessageAttachments(messages) {
                if (!messages.length) {
                    return [];
                }
                const messageIds = messages.map(({fields: {id}}) => id);
                const params = {
                    filter: `relatedId:${messageIds.join()}`,
                };
                const {items} = await this.$api(({files}) => files.getAllAttachments(params));
                return items.map(({fields}) => new AttachmentModel(fields));
            },
            async loadMore() {
                this.offset += this.limit;
                this.isProcessing = true;
                const {messages, total} = await this.generateCall();
                this.totalCount = total;
                this.isProcessing = false;
                // recompute hasMoreMessages
                this.displayedCount += this.limit;
                this.messages = [...this.messages, ...messages];
            },
            async postComment({comment, attachments}) {
                try {
                    this.isPostProcessing = true;
                    const message = await this.saveComment(comment);
                    this.messages.push(message);
                    if (attachments) {
                        try {
                            await this.saveMessageAttachments(message, attachments);
                        } catch (error) {
                            this.$toast.fromError(error, 'We were unable to attach your files. Please try again later or contact support.');
                        }
                    }
                    this.$toast.positive('Comment successfully posted');
                    this.isPostProcessing = false;
                } catch (error) {
                    this.$toast.fromError(error, 'We were unable to save your comment. Please try again later or contact support.');
                    this.isPostProcessing = false;
                }
            },
            async saveComment(message) {
                const params = {
                    id: this.entity.id,
                    data: {message},
                };
                const {fields} = await this.getDelegate(api => api.createTimelineComment(params));
                return new TimelineMessageModel(fields);
            },
            async showDeleteDialog(message) {
                const confirmDialog = createDialog(vConfirm, this);
                const dialogParams = {
                    title: 'Delete message',
                    content: 'Are you sure you want to delete this message?',
                    confirmLabel: 'Yes',
                    cancelLabel: 'No',
                };
                if (await confirmDialog(dialogParams)) {
                    this.deleteMessage(message);
                }
            },
            async deleteMessage(message) {
                const {id, attachments} = message;
                const params = {id: this.entity.id, messageId: id};
                this.messages = this.messages.filter(({id: messageId}) => messageId !== id);
                try {
                    if (attachments.length) {
                        await this.deleteMessageAttachments(message);
                    }
                    await this.getDelegate(api => api.deleteTimelineMessage(params));
                    this.$toast.positive('Message successfully deleted');
                } catch (error) {
                    this.messages.push(message);
                    this.$toast.fromError(error, 'We were unable to delete your message. Please try again later or contact support.');
                }
            },
            async saveMessageAttachments(message, attachments) {
                const promises = attachments.map(
                    async (file) => {
                        const attachment = new AttachmentModel(
                            {
                                relatedId: message.id,
                                blob: file,
                                name: file.name,
                                relatedType: `${this.type}-timeline-comment`,
                            },
                        );
                        message.attachments.push(attachment);
                        try {
                            attachment.isTransfering = true;
                            const {fields: uploadedFile} = await this.$api(({files}) => files.upload(attachment.blobFormWrapper));
                            attachment.file = uploadedFile;
                            const {fields: savedAttachment} = await this.$api(({files}) => files.attach({data: attachment}));
                            Object.assign(attachment, savedAttachment);
                            attachment.hasError = false;
                        } catch (error) {
                            attachment.hasError = true;
                            throw error;
                        } finally {
                            attachment.isTransfering = false;
                        }
                    },
                );
                return Promise.all(promises);
            },
            async downloadAttachment(attachment) {
                if (attachment.isTransfering) {
                    return;
                }
                const {fileId, name} = attachment;
                const params = {id: fileId};
                try {
                    attachment.isTransfering = true;
                    if (!attachment.blob || !attachment.mimeType) {
                        const {data, response} = await this.$api(
                            ({files}) => files.download(params),
                        );
                        attachment.blob = data;
                        attachment.mimeType = response && response.headers && response.headers['content-type'];
                    }
                    const media = new Media({
                        mimeType: attachment.mimeType,
                        data: attachment.blob,
                        fileName: name,
                    });
                    media.triggerDownload();
                    attachment.hasError = false;
                } catch (error) {
                    attachment.hasError = true;
                    this.$toast.fromError(error, 'We were unable to download your file. Please try again later or contact support.');
                } finally {
                    attachment.isTransfering = false;
                }
            },
            async deleteMessageAttachments(message) {
                const deleteAction = async ({fileId: id}) => {
                    try {
                        await this.$api(({files}) => files.detachAndDelete({id}));
                        message.attachments = message.attachments.filter(({fileId}) => fileId !== id);
                    } catch (error) {
                        throw error;
                    }
                };
                return Promise.all(message.attachments.filter(({fileId}) => fileId).map(deleteAction));
            },
        },
    };
</script>
